/**
 * EngShelves — 全社で一つの場所マスタ（建物 › 部屋 › 倉庫 › ゾーン › 棚 › ビン）。
 * B5 · 技術・製造・IT・備品が同じマスタを見る。既存の棚データと id はそのまま。
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Plus, PencilSimple, Trash, MagnifyingGlass, MapPin, Buildings, DoorOpen, Shapes, Package, Warehouse, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { engApi, LOCATION_TYPE_LABEL } from './engApi';
import MasterCleanupDialog from './MasterCleanupDialog';

const TYPES = ['building', 'room', 'warehouse', 'zone', 'shelf', 'bin'];
// コードが要る区分（サーバ側 `location_master.CODE_REQUIRED_TYPES` と同じ）
const CODE_REQUIRED_TYPES = ['warehouse', 'zone', 'shelf', 'bin'];
const TYPE_ICON = { building: Buildings, room: DoorOpen, warehouse: Warehouse, zone: MapPin, shelf: Shapes, bin: Package };
const TYPE_STYLE = {
  building: 'bg-slate-100 text-slate-700 border-slate-200',
  room: 'bg-sky-50 text-sky-700 border-sky-200',
  warehouse: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  zone: 'bg-violet-50 text-violet-700 border-violet-200',
  shelf: 'bg-teal-50 text-teal-700 border-teal-200',
  bin: 'bg-amber-50 text-amber-700 border-amber-200',
};
const SEED_LABEL = {
  copy_keep_id: '技術の場所を同じIDで写す', already_present: '既に取り込み済み',
  skip_test_row: 'テスト行をスキップ', tag_source: '出自タグを付ける',
  create: '新しい場所を作る', alias: '別名を付ける', alias_existing: '既存行に別名を付ける',
  drop: '作らない（未登録のまま）', copied_from_eng: '写した', tagged_mfg: 'タグ付け',
  created: '新規作成', aliased: '別名追加', alias_moved: '別名を正しい行へ移動',
  alias_target_missing: '対象が無く保留',
};

// 木を深さ付きの平らな配列にする（再帰コンポーネントを避ける）
function flattenTree(nodes, depth = 0, out = []) {
  for (const n of nodes || []) {
    out.push({ ...n, depth });
    flattenTree(n.children, depth + 1, out);
  }
  return out;
}

const SOURCE_LABEL = { mfg: '製造', eng: '技術', it: 'IT', bihin: '備品', eng_equipment: '設備台帳', manual: '手動' };

const emptyShelf = () => ({ id: '', name: '', code: '', location: '', notes: '', location_type: 'shelf', parent_id: '', dept_code: '', aliases: [] });

export default function EngShelves() {
  const ctx = useOutletContext?.() || {};
  const { user } = useAuth();
  // 設定 → 場所 では技術のレイアウトの外に居るので、ここで自分で権限を見る。
  // （以前はレイアウトの文脈だけを見ていたため、管理者でも追加・編集が出なかった）
  const isAdmin = !!(user?.role === 'admin' || user?.is_admin);
  const access = ctx.access || { is_member: isAdmin, is_admin: isAdmin };
  const canEdit = access.is_admin || access.is_member || isAdmin;
  const [deptFilter, setDeptFilter] = useState('all');
  const [deptOptions, setDeptOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(emptyShelf());
  const [saving, setSaving] = useState(false);
  // B5 · 旧リストの取り込み（管理者のみ・ドライランを見てから確定）
  const [seedPlan, setSeedPlan] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  // 無効化・統合済みの行は既定で隠す（一覧が古い行で埋まらないように）
  const [showHidden, setShowHidden] = useState(false);
  const [codeHint, setCodeHint] = useState('');
  const [aliasInput, setAliasInput] = useState('');

  const openSeed = async () => {
    setSeedBusy(true);
    try { setSeedPlan(await engApi.locationsSeedPlan()); }
    catch (e) { toast.error(e?.response?.data?.detail || '取り込み計画を取得できませんでした'); }
    finally { setSeedBusy(false); }
  };
  const applySeed = async () => {
    setSeedBusy(true);
    try {
      const out = await engApi.locationsSeedApply();
      const n = Object.entries(out.applied || {}).map(([k, v]) => `${k} ${v}`).join(' / ');
      toast.success(`取り込みました（${n || '変更なし'}）`);
      setSeedPlan(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '取り込みに失敗しました'); }
    finally { setSeedBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_all: true, include_hidden: showHidden, with_usage: true };
      if (deptFilter !== 'all') params.dept_code = deptFilter;
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/locations`, { params });
      setRows(data || []);
    } catch (_e) { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [deptFilter, showHidden]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    engApi.locationDepartments().then((d) => {
      setDeptOptions(d.departments || []);
      setCodeHint(d.code_rule_hint || '');
    }).catch(() => setDeptOptions([]));
  }, []);

  const suggestCode = async () => {
    try {
      const purpose = /完成/.test(draft.name || '') ? 'finished'
        : (/入荷|受入|資材/.test(draft.name || '') ? 'receiving' : 'shelf');
      const out = await engApi.locationNextCode(draft.dept_code, purpose);
      if (out.code) setDraft((d) => ({ ...d, code: out.code }));
      if (out.code && !out.free) toast.warning(`${out.code} は既に使われています`);
    } catch (e) { toast.error('コードを提案できませんでした'); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const n = q.trim().toLowerCase();
    return rows.filter((r) => `${r.name || ''} ${r.code || ''} ${r.path || ''}`.toLowerCase().includes(n));
  }, [rows, q]);

  // 親候補 — 自分より上位の階層のみ（自分自身は除外）
  const parentOptions = useMemo(() => {
    const idx = TYPES.indexOf(draft.location_type || 'shelf');
    return rows.filter((r) => r.id !== draft.id && TYPES.indexOf(r.location_type || 'shelf') < idx);
  }, [rows, draft.location_type, draft.id]);

  const openNew = (parent) => {
    if (parent) {
      const childType = TYPES[Math.min(TYPES.indexOf(parent.location_type || 'shelf') + 1, TYPES.length - 1)];
      setDraft({ ...emptyShelf(), location_type: childType, parent_id: parent.id });
    } else {
      setDraft(emptyShelf());
    }
    setEditOpen(true);
  };
  const openEdit = (row) => {
    setDraft({ ...emptyShelf(), ...row, parent_id: row.parent_id || '',
      aliases: row.aliases || [] });
    setAliasInput('');
    setEditOpen(true);
  };

  const save = async () => {
    const name = (draft.name || '').trim();
    if (!name) { toast.error('名称を入力してください'); return; }
    if (!draft.dept_code) {
      toast.error('部署を選んでください（SNS / SOL / HTR / 技術 / IT / 共通）'); return;
    }
    if (CODE_REQUIRED_TYPES.includes(draft.location_type) && !(draft.code || '').trim()) {
      toast.error(`「${LOCATION_TYPE_LABEL[draft.location_type]}」にはコードが必要です`); return;
    }
    if (draft.location_type !== 'building' && !draft.parent_id) {
      toast.error(`「${LOCATION_TYPE_LABEL[draft.location_type]}」には親の場所を選択してください`); return;
    }
    setSaving(true);
    try {
      const body = { name, code: (draft.code || '').trim() || null,
        notes: (draft.notes || '').trim() || null,
        location_type: draft.location_type || 'shelf',
        dept_code: draft.dept_code || '',
        aliases: (draft.aliases || []).map((a) => a.trim()).filter(Boolean),
        parent_id: draft.parent_id || null };
      const res = draft.id ? await engApi.locationUpdate(draft.id, body)
        : await engApi.locationCreate(body);
      toast.success(draft.id ? '更新しました' : '登録しました');
      if (res?.same_name_warning) {
        toast.warning(`同じ部署に同じ名前の場所があります: ${res.same_name_warning.path}`,
          { duration: 10000 });
      }
      setEditOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    const next = !(row.active !== false);
    if (!next && !window.confirm(
      `「${row.path || row.name}」を無効にします。\n`
      + '記録は消えません。新しい登録・出庫でこの場所は選べなくなります。よろしいですか？')) return;
    try {
      await engApi.locationSetActive(row.id, next);
      toast.success(next ? '有効にしました' : '無効にしました');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '変更に失敗しました'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`「${row.path || row.name}」を削除してもよろしいですか？`)) return;
    try {
      await engApi.deleteShelf(row.id);
      toast.success('削除しました');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '削除に失敗しました'); }
  };

  return (
    <div className="space-y-4" data-testid="eng-shelves-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <MapPin size={20} weight="duotone" className="text-teal-600" />
            <h1 className="text-lg font-bold text-zinc-900">場所マスタ</h1>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            建物 › 部屋 › 倉庫 › ゾーン › 棚 › ビン の階層で登録します。
            <span className="font-semibold text-zinc-600">技術・製造・IT・備品で共通の場所マスタ</span>です。
            ここに登録された場所が各台帳の「現在地・設置場所・保管場所」として選べます。
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {access.is_admin && (
            <Button variant="outline" onClick={() => setCleanupOpen(true)}
              data-testid="master-cleanup-btn" className="h-9">
              マスタの片付け
            </Button>
          )}
          {access.is_admin && (
            <Button variant="outline" onClick={openSeed} disabled={seedBusy}
              data-testid="eng-shelf-seed-btn" className="h-9">
              旧リストを取り込む
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => openNew(null)} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-shelf-add-btn">
              <Plus size={14} weight="bold" className="mr-1" /> 場所を追加
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="location-dept-chips">
        {[{ code: 'all', label: 'すべて' }, ...deptOptions].map((d) => (
          <button key={d.code} type="button" onClick={() => setDeptFilter(d.code)}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
              deptFilter === d.code
                ? 'border-teal-600 bg-teal-600 text-white'
                : 'border-zinc-200 bg-white text-zinc-600 hover:border-teal-300'}`}
            data-testid={`location-dept-chip-${d.code}`}>
            {d.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="名称・コード・パスで検索…" className="h-9 pl-8"
            data-testid="eng-shelf-search" />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-zinc-600">
          <input type="checkbox" className="h-3.5 w-3.5 accent-teal-600"
            checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)}
            data-testid="location-show-hidden" />
          無効も表示（統合・無効化した場所）
        </label>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="eng-shelf-table">
          <thead className="bg-zinc-50 text-zinc-600 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-semibold min-w-[180px]">場所</th>
              <th className="text-left px-3 py-2 font-semibold whitespace-nowrap w-[72px]">区分</th>
              <th className="text-left px-3 py-2 font-semibold whitespace-nowrap w-[80px]">部署</th>
              <th className="text-left px-3 py-2 font-semibold whitespace-nowrap w-[110px]">コード</th>
              <th className="text-right px-3 py-2 font-semibold whitespace-nowrap w-[60px]">件数</th>
              <th className="text-left px-3 py-2 font-semibold w-[120px]">備考</th>
              <th className="w-[96px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-zinc-400">読込中…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-zinc-500">
                {q ? '該当なし' : 'まだ場所が登録されていません。'}
              </td></tr>
            )}
            {!loading && filtered.map((row) => {
              const Icon = TYPE_ICON[row.location_type] || Shapes;
              return (
                <tr key={row.id} className="hover:bg-zinc-50" data-testid={`eng-shelf-row-${row.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2" style={{ paddingLeft: `${(row.depth || 0) * 18}px` }}>
                      <Icon size={15} weight="duotone" className="text-zinc-400 shrink-0" />
                      <span className="font-medium text-zinc-800 whitespace-nowrap">{row.name}</span>
                      {row.depth > 0 && <span className="text-[11px] text-zinc-400 truncate">{row.path}</span>}
                      {row.source && row.source !== 'eng' && (
                        <span className="text-[10px] text-zinc-400 shrink-0">{SOURCE_LABEL[row.source] || row.source}</span>
                      )}
                      {(row.aliases || []).length > 0 && (
                        <span className="text-[10px] text-zinc-400 truncate" title={row.aliases.join('、')}>
                          別名: {row.aliases.join('、')}
                        </span>
                      )}
                      {row.merged_into && (
                        <span className="shrink-0 rounded border border-zinc-300 bg-zinc-100 px-1 text-[10px] font-semibold text-zinc-500"
                          data-testid={`location-merged-${row.id}`}>統合済み</span>
                      )}
                      {row.active === false && !row.merged_into && (
                        <span className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1 text-[10px] font-semibold text-amber-700"
                          data-testid={`location-inactive-${row.id}`}>無効</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-flex justify-center min-w-[38px] rounded border px-1.5 py-0.5 text-[10px] font-semibold ${TYPE_STYLE[row.location_type] || TYPE_STYLE.shelf}`}>
                      {LOCATION_TYPE_LABEL[row.location_type] || '棚'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600"
                      data-testid={`location-dept-${row.id}`}>
                      {row.dept_code || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap text-zinc-500">{row.code || '—'}</td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums text-zinc-500"
                    data-testid={`location-usage-${row.id}`}>{row.usage_count || 0}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">
                    <span className="block max-w-[120px] truncate" title={row.notes || ''}>{row.notes || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canEdit && (
                      <>
                        {row.location_type !== 'bin' && (
                          <button onClick={() => openNew(row)}
                            className="inline-flex items-center px-1.5 py-1 text-zinc-500 hover:text-teal-700 rounded"
                            title="配下に追加" data-testid={`eng-shelf-addchild-${row.id}`}>
                            <Plus size={14} />
                          </button>
                        )}
                        <button onClick={() => openEdit(row)}
                          className="inline-flex items-center px-1.5 py-1 text-zinc-500 hover:text-teal-700 rounded"
                          title="編集" data-testid={`eng-shelf-edit-${row.id}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => toggleActive(row)}
                          className="inline-flex items-center px-1.5 py-1 text-zinc-500 hover:text-amber-700 rounded"
                          title={row.active === false ? '有効にする' : '無効にする（一覧と選択肢から隠す）'}
                          data-testid={`location-toggle-${row.id}`}>
                          {row.active === false ? <Eye size={14} /> : <EyeSlash size={14} />}
                        </button>
                        <button onClick={() => remove(row)}
                          className="inline-flex items-center px-1.5 py-1 text-zinc-500 hover:text-rose-600 rounded"
                          title="削除" data-testid={`eng-shelf-delete-${row.id}`}>
                          <Trash size={14} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <MasterCleanupDialog open={cleanupOpen} onClose={() => setCleanupOpen(false)}
        onApplied={load} />

      <Dialog open={!!seedPlan} onOpenChange={(o) => !o && setSeedPlan(null)}>        <DialogContent className="sm:max-w-2xl" data-testid="eng-shelf-seed-dialog">
          <DialogHeader><DialogTitle>旧リストの取り込み（確認）</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">
            技術の場所マスタ・IT の設置場所・備品と設備台帳の自由入力を、共通の場所マスタへ寄せます。
            <span className="font-semibold text-zinc-700">既存の行は名前も親も部署も書き換えません</span>
            （写す・出自タグ・別名の追加だけ）。確定するまで1行も保存しません。何度でも実行できます。
          </p>
          <div className="flex flex-wrap gap-2" data-testid="eng-shelf-seed-counts">
            {Object.entries(seedPlan?.counts || {}).map(([k, v]) => (
              <span key={k} className="rounded border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-600">
                {SEED_LABEL[k] || k}: <span className="font-semibold text-zinc-800">{v}</span>
              </span>
            ))}
          </div>
          <div className="max-h-56 overflow-auto rounded border border-zinc-200 p-2 text-[11px] leading-5"
            data-testid="eng-shelf-seed-tree">
            {flattenTree(seedPlan?.tree).map((n) => (
              <div key={n.id} style={{ paddingLeft: `${n.depth * 14}px` }} className="text-zinc-700">
                {n.name}
                <span className="ml-1 text-zinc-400">{LOCATION_TYPE_LABEL[n.location_type] || ''}</span>
                {n.department_name && <span className="ml-1 text-zinc-400">[{n.department_name}]</span>}
                {(n.aliases || []).length > 0 && <span className="ml-1 text-teal-700">別名: {n.aliases.join('、')}</span>}
              </div>
            ))}
          </div>
          <div className="max-h-40 overflow-auto text-[11px] text-zinc-500 space-y-0.5">
            {(seedPlan?.plan || []).filter((p) => p.action !== 'skip_test_row' && p.action !== 'tag_source').map((p, i) => (
              <div key={i}>
                <span className="font-semibold text-zinc-700">{SEED_LABEL[p.action] || p.action}</span>
                {' — '}{p.name || p.text}{p.target ? ` → ${p.target}` : ''}
                {p.resolvable === false && <span className="text-amber-700">（対象が未登録のためスキップ）</span>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedPlan(null)} data-testid="eng-shelf-seed-cancel">キャンセル</Button>
            <Button onClick={applySeed} disabled={seedBusy} className="bg-teal-700 hover:bg-teal-800"
              data-testid="eng-shelf-seed-apply">取り込みを実行</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(o) => !o && setEditOpen(false)}>
        <DialogContent aria-describedby={undefined} data-testid="eng-shelf-dialog">
          <DialogHeader>
            <DialogTitle>{draft.id ? '場所を編集' : '場所を追加'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">
                部署 <span className="text-rose-500">＊</span>
              </Label>
              <select value={draft.dept_code || ''}
                onChange={(e) => setDraft((d) => ({ ...d, dept_code: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                data-testid="eng-shelf-form-dept">
                <option value="">選択してください</option>
                {deptOptions.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
              </select>
              <p className="mt-0.5 text-[10px] text-zinc-500">
                共通を選ぶと、どの部署の画面からも選べます（棚・ビン・ゾーン・倉庫はコードが必要です）。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">階層区分 <span className="text-rose-500">＊</span></Label>
                <select value={draft.location_type}
                  onChange={(e) => setDraft((d) => ({ ...d, location_type: e.target.value, parent_id: e.target.value === 'building' ? '' : d.parent_id }))}
                  className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                  data-testid="eng-shelf-form-type">
                  {TYPES.map((t) => <option key={t} value={t}>{LOCATION_TYPE_LABEL[t]}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">
                  親の場所 {draft.location_type !== 'building' && <span className="text-rose-500">＊</span>}
                </Label>
                <select value={draft.parent_id || ''} onChange={(e) => setDraft((d) => ({ ...d, parent_id: e.target.value }))}
                  disabled={draft.location_type === 'building'}
                  className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm disabled:bg-zinc-100"
                  data-testid="eng-shelf-form-parent">
                  <option value="">{draft.location_type === 'building' ? '（最上位）' : '選択してください'}</option>
                  {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.path}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label className="text-xs font-semibold text-zinc-600">コード</Label>
                <div className="mt-1 flex gap-1">
                  <Input value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
                    placeholder="例: SOL-01" className="font-mono" data-testid="eng-shelf-form-code" />
                  <button type="button" onClick={suggestCode}
                    className="shrink-0 rounded-md border border-zinc-200 px-2 text-[10px] font-semibold text-teal-700 hover:border-teal-300"
                    data-testid="eng-shelf-code-suggest">空き番号</button>
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-zinc-600">名称 <span className="text-rose-500">＊</span></Label>
                <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="例: 技術2 / A-03" className="mt-1" data-testid="eng-shelf-form-name" />
              </div>
            </div>
            {codeHint && (
              <p className="text-[10px] leading-relaxed text-zinc-500" data-testid="eng-shelf-code-hint">
                {codeHint}
              </p>
            )}
            <div>
              <Label className="text-xs font-semibold text-zinc-600">別名（旧い呼び名・旧コード）</Label>
              <div className="mt-1 flex flex-wrap gap-1" data-testid="eng-shelf-form-aliases">
                {(draft.aliases || []).map((a) => (
                  <span key={a} className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-600">
                    {a}
                    <button type="button" aria-label={`${a} を外す`}
                      onClick={() => setDraft((d) => ({ ...d, aliases: d.aliases.filter((x) => x !== a) }))}
                      className="text-zinc-400 hover:text-rose-600"
                      data-testid={`eng-shelf-alias-remove-${a}`}>×</button>
                  </span>
                ))}
                {(draft.aliases || []).length === 0 && <span className="text-[11px] text-zinc-400">なし</span>}
              </div>
              <div className="mt-1 flex gap-1">
                <Input value={aliasInput} onChange={(e) => setAliasInput(e.target.value)}
                  placeholder="旧い呼び名を足す（例: 本社 倉庫）" className="h-9"
                  data-testid="eng-shelf-alias-input" />
                <Button type="button" variant="outline" className="h-9 shrink-0"
                  onClick={() => {
                    const v = aliasInput.trim();
                    if (!v) return;
                    setDraft((d) => ({ ...d, aliases: Array.from(new Set([...(d.aliases || []), v])) }));
                    setAliasInput('');
                  }}
                  data-testid="eng-shelf-alias-add">追加</Button>
              </div>
              <p className="mt-0.5 text-[10px] text-zinc-500">
                別名は消えません。名称やコードを変えると、旧い値が自動で別名に入ります
                （印刷済みラベルや古い記録からも引けるように）。
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">補足（自由記述）</Label>
              <Input value={draft.location} onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
                placeholder="例: 本社2F 技術倉庫" className="mt-1" data-testid="eng-shelf-form-location" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">備考</Label>
              <Textarea rows={2} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                className="mt-1" data-testid="eng-shelf-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>キャンセル</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-shelf-form-save">
              {saving ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
